import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  IconButton,
  Box,
  TextField,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import CalculateIcon from "@mui/icons-material/Calculate";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useCalculator } from "../../contexts/CalculatorContext";
import { useTranslation } from "react-i18next";

/**
 * AddToCalculatorButton component for adding products to calculator
 * @param {Object} props - Component props
 * @returns {JSX.Element} AddToCalculatorButton component
 */
const AddToCalculatorButton = ({
  product,
  variation,
  stock = 0,
  variant = "outlined",
  size = "medium",
  fullWidth = false,
  showQuantity = true,
  sx = {},
  ...props
}) => {
  const { t } = useTranslation("common");
  const { t: tProducts } = useTranslation("products");
  const { addToCalculator, getCartQuantityForVariation } = useCalculator();
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);

  // Get effective stock (available stock minus cart quantity)
  const cartQuantity = variation
    ? getCartQuantityForVariation(variation.id)
    : 0;
  const effectiveStock = Math.max(0, stock - cartQuantity);

  // Handle quantity change
  const handleQuantityChange = (event) => {
    const value = parseInt(event.target.value);
    if (!isNaN(value) && value >= 1 && value <= effectiveStock) {
      setQuantity(value);
    }
  };

  // Handle increment quantity
  const handleIncrement = () => {
    if (quantity < effectiveStock) {
      setQuantity(quantity + 1);
    }
  };

  // Handle decrement quantity
  const handleDecrement = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  // Handle add to calculator
  const handleAddToCalculator = () => {
    if (effectiveStock <= 0 || !variation) return;

    setLoading(true);
    try {
      // Add to calculator
      addToCalculator(product, variation, quantity);
      // Reset quantity after adding
      setQuantity(1);
    } finally {
      setLoading(false);
    }
  };

  // If out of stock or no variation selected, disable button
  const isOutOfStock = effectiveStock <= 0;
  const isDisabled = isOutOfStock || loading || !variation;

  return (
    <Box sx={{ display: "flex", alignItems: "center", ...sx }}>
      {showQuantity && (
        <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
          <Tooltip title={tProducts("shared.quantity.decrement")}>
            <span>
              <IconButton
                size="small"
                onClick={handleDecrement}
                disabled={quantity <= 1 || isDisabled}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <TextField
            size="small"
            value={quantity}
            onChange={handleQuantityChange}
            inputProps={{
              min: 1,
              max: effectiveStock,
              style: { textAlign: "center" },
            }}
            disabled={isDisabled}
            sx={{ width: "60px", mx: 1 }}
          />

          <Tooltip title={tProducts("shared.quantity.increment")}>
            <span>
              <IconButton
                size="small"
                onClick={handleIncrement}
                disabled={quantity >= effectiveStock || isDisabled}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      <Button
        variant={variant}
        color="secondary"
        size={size}
        fullWidth={fullWidth}
        startIcon={<CalculateIcon />}
        onClick={handleAddToCalculator}
        disabled={isDisabled}
        sx={{ position: "relative" }}
      >
        {loading ? (
          <CircularProgress size={24} sx={{ position: "absolute" }} />
        ) : isOutOfStock ? (
          tProducts("shared.status.outOfStock")
        ) : !variation ? (
          tProducts("details.selectOptions")
        ) : props.buttonText ? (
          props.buttonText
        ) : (
          tProducts("details.addToCalculator")
        )}
      </Button>
    </Box>
  );
};

AddToCalculatorButton.propTypes = {
  /** Parent product object */
  product: PropTypes.object.isRequired,
  /** Selected variation object */
  variation: PropTypes.object,
  /** Available stock */
  stock: PropTypes.number,
  /** Button variant */
  variant: PropTypes.oneOf(["contained", "outlined", "text"]),
  /** Button size */
  size: PropTypes.oneOf(["small", "medium", "large"]),
  /** Whether the button should take full width */
  fullWidth: PropTypes.bool,
  /** Whether to show quantity controls */
  showQuantity: PropTypes.bool,
  /** Button text */
  buttonText: PropTypes.string,
  /** Additional styles */
  sx: PropTypes.object,
};

export default AddToCalculatorButton;
